import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, isModeAllowed, getCurrentUser } from "@/lib/api";
import { staffHomePath } from "@/lib/staffAccess";
import { useFeatures } from "@/lib/features";
import OperatorSwitcher from "@/components/OperatorSwitcher";
import {
  UtensilsCrossed, Sparkles, Store, Shirt, Pizza, Coffee, IceCream, Croissant,
  Cigarette, Newspaper, Scissors, ShoppingCart, Beef, Wine, Car, BedDouble, ArrowLeft, Dog
} from "lucide-react";

const ICON_MAP = {
  ristorante: UtensilsCrossed, festa: Sparkles, negozio: Store, lavanderia: Shirt,
  pizzeria: Pizza, bar: Coffee, gelateria: IceCream, panetteria: Croissant,
  tabacchi: Cigarette, edicola: Newspaper, parrucchiere: Scissors,
  alimentari: ShoppingCart, macelleria: Beef, enoteca: Wine, autolavaggio: Car, bnb: BedDouble, alloggio_dedicato: BedDouble,
  toelettatura: Scissors,
  negozio_animali: Dog,
};

const COLOR_MAP = {
  ristorante: "#B73C27", festa: "#18181B", negozio: "#18181B", lavanderia: "#18181B",
  pizzeria: "#B73C27", bar: "#18181B", gelateria: "#B73C27", panetteria: "#18181B",
  tabacchi: "#18181B", edicola: "#18181B", parrucchiere: "#18181B",
  alimentari: "#18181B", macelleria: "#B73C27", enoteca: "#18181B", autolavaggio: "#18181B", bnb: "#18181B", alloggio_dedicato: "#18181B",
  toelettatura: "#18181B",
  negozio_animali: "#18181B",
};

const ROUTE_MAP = {
  ristorante: "/cassa/ristorante", festa: "/cassa/festa", negozio: "/cassa/negozio",
  negozio_animali: "/cassa/negozio_animali",
  lavanderia: "/cassa/lavanderia", pizzeria: "/cassa/pizzeria", parrucchiere: "/cassa/parrucchiere",
  alimentari: "/cassa/alimentari", macelleria: "/cassa/macelleria", panetteria: "/cassa/panetteria",
  tabacchi: "/cassa/tabacchi", edicola: "/cassa/edicola", enoteca: "/cassa/enoteca",
  gelateria: "/cassa/gelateria", autolavaggio: "/cassa/autolavaggio", bnb: "/cassa/bnb",
  alloggio_dedicato: "/dashboard/alberghiero?tab=guests",
  bar: "/cassa/bar", toelettatura: "/cassa/toelettatura",
};

const fallbackRoute = "/cassa/negozio";

export default function ModeSelector() {
  const nav = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [modes, setModes] = useState([]);
  const { hasFeature, features, modes: featModes } = useFeatures();

  useEffect(() => {
    api.get("/tenant/me").then((r) => setTenant(r.data && typeof r.data === "object" ? r.data : { modes: [] })).catch(() => setTenant({ modes: [] }));
    api.get("/modes").then((r) => setModes(Array.isArray(r.data) ? r.data : [])).catch(() => setModes([]));
  }, []);

  const tenantModeList = Array.isArray(tenant?.modes) && tenant.modes.length
    ? tenant.modes
    : (Array.isArray(featModes) && featModes.length ? featModes : (tenant?.mode ? [tenant.mode] : []));

  const visibleModes = (Array.isArray(tenantModeList) ? tenantModeList : []).filter(m => isModeAllowed(m));
  const enabledModes = (Array.isArray(modes) ? modes : []).filter(m => visibleModes.includes(m.id));
  const isStaff = getCurrentUser()?.role === "client_staff";
  const backTo = isStaff ? staffHomePath(getCurrentUser()) : "/dashboard";
  const backLabel = isStaff ? "Indietro" : "Gestionale";

  return (
    <div className="min-h-screen bg-[#F7F7F8]">
      <header className="border-b border-[#E4E4E7] bg-white">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to={backTo} data-testid="pos-back-link" className="inline-flex items-center gap-2 text-[#71717A] hover:text-[#18181B] text-sm font-medium">
            <ArrowLeft className="w-4 h-4" /> {backLabel}
          </Link>
          <div className="flex items-center gap-3">
            <OperatorSwitcher compact />
            <div className="flex items-center gap-2">
              <img src="/velta-one-mark.png" alt="Velta One" className="w-11 h-6 object-contain" data-testid="pos-menu-logo" />
              <div className="font-heading font-semibold tracking-tight text-[#18181B]">Cassa</div>
            </div>
          </div>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-6 py-12">
        {(Array.isArray(features) ? features : []).length > 0 && !hasFeature("pos") ? (
          <div data-testid="pos-module-disabled" className="bg-white border border-[#E4E4E7] rounded-2xl p-12 text-center max-w-lg mx-auto mt-8">
            <div className="font-heading text-2xl font-semibold mb-2 text-[#18181B]">Modulo Cassa non attivo</div>
            <p className="text-[#71717A] text-sm">Il modulo Casse non è incluso nel tuo contratto. Contatta VELTA SISTEMI per attivarlo.</p>
          </div>
        ) : (
        <>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-[#18181B] mb-8">Scegli la cassa</h1>

        {enabledModes.length === 0 ? (
          <div className="bg-white border border-[#E4E4E7] rounded-2xl p-12 text-center text-[#71717A]">
            Nessuna modalità abilitata sulla tua licenza.
          </div>
        ) : (
          <div className={`grid gap-4 ${enabledModes.length === 1 ? "max-w-md" : "sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"}`}>
            {(Array.isArray(enabledModes) ? enabledModes : []).map((m) => {
              const Icon = ICON_MAP[m.id] || Store;
              const color = COLOR_MAP[m.id] || "#18181B";
              const route = ROUTE_MAP[m.id] || fallbackRoute;
              const label = tenant?.mode_labels?.[m.id]?.trim() || m.label;
              return (
                <button
                  key={m.id}
                  data-testid={`pos-mode-${m.id}`}
                  title={m.description || label}
                  onClick={() => nav(route)}
                  className="group text-left bg-white border border-[#E4E4E7] rounded-2xl p-6 transition-all hover:border-[#18181B] active:scale-[0.98] cursor-pointer min-h-[8.5rem] flex flex-col justify-between"
                >
                  <div className="flex items-start justify-between">
                    <div className="w-14 h-14 flex items-center justify-center rounded-xl text-white" style={{ background: color }}>
                      <Icon className="w-7 h-7" strokeWidth={1.8} />
                    </div>
                    {!ROUTE_MAP[m.id] && <span className="text-[10px] uppercase tracking-widest text-[#A1A1AA]">generica</span>}
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-[#18181B] mt-6">{label}</h3>
                </button>
              );
            })}
          </div>
        )}
        </>
        )}
      </main>
    </div>
  );
}
